import Alert from '@components/common/Alert';
import { Typography } from '@components/common/Typography';
import styled from 'styled-components';
import AlertIcon from '../../../public/icon-alert.svg';
import { Wrapper as TooltipWrapper, InfoWrapper } from '@components/common/Tooltip.styles';
import { rgba } from 'polished';

export const StyledForm = styled.form`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 2.5rem;
`;

export const StyledAlert = styled(Alert)`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

export const Title = styled.h4`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 8px;
`;

export const Field = styled.fieldset<{ variant?: string; includeError?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: ${({ includeError }) => (includeError ? '8px' : '24px')};
  position: relative;
  border-radius: 12px;
  padding: 8px;
  padding-left: 20px;
  background-color: ${({ theme }) => theme.colors.functional.gray3};
  ${({
    variant,
    theme: {
      colors: {
        primary: { lightest },
        functional: { gray3 },
        secondary: { paleRed, red },
      },
      boxShadow,
    },
  }) => {
    switch (variant) {
      case 'blue':
        return `background-color: ${lightest};`;
      case 'error':
        return `
      background-color: ${paleRed};
      Input {
        color: ${red};
      }
    `;
      default:
        return `background-color: ${rgba(gray3, 0.5)};`;
    }
  }};
`;

export const Input = styled.input`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary.main};
  margin: 0;
  width: 100%;
  line-height: 72px;
  height: 72px;
  box-shadow: none !important;
  border: 0 !important;
  background-color: transparent;
  border-radius: ${({ theme }) => theme.borderRadius.main};
  :focus {
    outline: none;
  }

  /* Hide arrows */
  :-webkit-outer-spin-button,
  :-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

export const Dropdown = styled.div<{ disabled?: boolean; noAsset?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  cursor: pointer;
  justify-content: center;
  border-radius: 12px;
  padding: 16px;
  position: relative;
  padding-left: 44px;
  padding-right: 28px;
  box-shadow: ${({ theme }) => theme.boxShadow.blue};
  background-color: ${({ theme }) => theme.colors.functional.white};
  ${({ noAsset }) => noAsset && `padding-left: 16px;`}
  ${({
    disabled,
    theme: {
      colors: { functional },
    },
  }) =>
    disabled &&
    `
  cursor: auto;
  box-shadow: none;
  ${DropdownChevron} {
    color: ${functional.darkGray};
    fill: ${functional.darkGray};
  }
  `}
`;

export const DropdownChevron = styled.span`
  position: absolute;
  display: block;
  height: 24px;
  width: 24px;
  right: 8px;
  top: 50%;
  margin-top: -12px;
  color: ${({ theme }) => theme.colors.primary.main};
  fill: ${({ theme }) => theme.colors.primary.main};
`;

/// TODO: make this an image instead of bg color
export const DropdownAssetIcon = styled.span`
  position: absolute;
  height: 24px;
  line-height: 24px;
  width: 24px;
  border-radius: 100%;
  display: block;
  background-color: ${({ theme }) => theme.colors.secondary.green};
  left: 16px;
  top: 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.functional.white};
`;

export const MaxButton = styled.button<{ disabled?: boolean }>`
  font-size: 1.6rem;
  color: ${({ theme }) => theme.colors.primary.main};
  ${({
    disabled,
    theme: {
      colors: {
        functional: { darkGray },
      },
    },
  }) =>
    disabled &&
    `
    color: ${darkGray};
    cursor: auto;
  `}
`;

export const RouteFeeButton = styled.button`
  color: ${({ theme }) => theme.colors.functional.darkGray};
  text-decoration: underline;
  font-size: 1.4rem;
  margin-bottom: 32px;
  fill: ${({ theme }) => theme.colors.primary.main};
  svg {
    width: 14px;
    height: 14px;
    display: inline-block;
    margin-left: 4px;
  }
`;

export const InputContainer = styled.div`
  width: 30%;
`;

export const DropdownContainer = styled.div<{ withMarginLeft?: boolean; size?: 'small' | 'medium' }>`
  display: flex;
  width: 100%;
  height: 76px;
  overflow: visible;
  position: relative;
  max-width: 263px;
  justify-self: end;
  margin-left: auto;
  ${TooltipWrapper} {
    position: absolute;
    height: 18px;
    right: 11px;
    top: 50%;
    margin-top: -10px;
  }
  ${InfoWrapper} {
    opacity: 0;
  }
  ${({ withMarginLeft }) => withMarginLeft && `margin-left: 8px;`};
  ${({ size }) => size && `max-width: ${size === 'small' ? '111px' : '144px'};`};
`;

export const ButtonContainer = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

export const StyledDropdown = styled(Dropdown)<{ withMarginLeft?: boolean }>`
  ${({ withMarginLeft }) => withMarginLeft && `margin-left: 8px`}
`;

export const StyledSpan = styled(Typography)`
  font-family: ${({ theme }) => theme.fonts.archivo};
  color: ${({ theme }) => theme.colors.functional.darkGray};
  font-size: 14px;
  line-height: 18px;
`;

export const StyledAlertIcon = styled(AlertIcon)`
  width: 24px;
  height: 24px;
  margin-right: 8px;
  path {
    fill: ${({ theme }) => theme.colors.secondary.red};
  }
`;

export const StrategyAndLeverageContainer = styled.div`
  display: flex;
  max-width: 263px;
  margin-left: auto;
`;

export const ErrorContainer = styled.div`
  margin-bottom: 32px;
`;

export const ErrorMessage = styled(Typography)`
  font-family: ${({ theme }) => theme.fonts.archivo};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.secondary.red};
`;
